import { useEffect, useRef, useState } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import { MasterIndexApi } from '@/api';
import { NoDataError } from '@/components/NoDataError/NoDataError';
import { Badge, Skeleton } from '@/components/TailwindComponents/base';
import { cn } from '@/components/TailwindComponents/lib/utils';

import {
  personArrestDataGridCols,
  personCaseDataGridCols,
  personCfsDataGridCols,
  personDirDataGridCols,
  personTaskDataGridCols,
  personWarrantDataGridCols,
} from '../data';
import { RelatedRecordSection } from './RelatedRecordSection';

interface PersonRelatedRecordsProps {
  recordId: string;
}

interface RecordCounts {
  cfs: number;
  arrests: number;
  cases: number;
  warrants: number;
  tasks: number;
  dir: number;
}

const SECTIONS = [
  {
    key: 'cfs',
    label: 'CFS',
    gridType: 'personCfs',
    colSettings: personCfsDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedCfs,
    storageKey: 'person-related-cfs',
  },
  {
    key: 'arrests',
    label: 'Arrests',
    gridType: 'personArrests',
    colSettings: personArrestDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedArrests,
    storageKey: 'person-related-arrests',
  },
  {
    key: 'cases',
    label: 'Cases',
    gridType: 'personCases',
    colSettings: personCaseDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedCases,
    storageKey: 'person-related-cases',
  },
  {
    key: 'warrants',
    label: 'Warrants',
    gridType: 'personWarrants',
    colSettings: personWarrantDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedWarrants,
    storageKey: 'person-related-warrants',
  },
  {
    key: 'tasks',
    label: 'Tasks',
    gridType: 'personTasks',
    colSettings: personTaskDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedTasks,
    storageKey: 'person-related-tasks',
  },
  {
    key: 'dir',
    label: 'DIR',
    gridType: 'personDir',
    colSettings: personDirDataGridCols,
    fetchFn: MasterIndexApi.getPersonRelatedDir,
    storageKey: 'person-related-dir',
  },
] as const;

export const PersonRelatedRecords = ({
  recordId,
}: PersonRelatedRecordsProps) => {
  const [counts, setCounts] = useState<RecordCounts | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    MasterIndexApi.getPersonRelatedRecordCounts(recordId)
      .then((res) => {
        if (!cancelled) {
          setCounts(res.data);
          const nonEmpty = SECTIONS.filter(
            (s) => (res.data[s.key as keyof RecordCounts] ?? 0) > 0,
          ).map((s) => s.key);
          setExpandedSections(nonEmpty);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  const sectionCaches = useRef<
    Map<string, { rows: any[]; totalCount: number }>
  >(new Map());

  if (!counts) {
    return (
      <div className="flex flex-col flex-1 p-4 overflow-auto">
        <div className="bg-white border border-gray-200 rounded-lg">
          {[0, 1].map((i) => (
            <div key={i} className={cn(i === 0 && 'border-b border-gray-200')}>
              <div className="flex items-center gap-2 px-4 py-3">
                <Skeleton className="w-4 h-4 rounded" />
                <Skeleton className="w-24 h-4" />
              </div>
              <div className="px-4 pb-3">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-4">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <Skeleton key={j} className="flex-1 h-4" />
                    ))}
                  </div>
                  <div className="flex gap-4">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <Skeleton key={j} className="flex-1 h-4" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const visibleSections = SECTIONS.filter(
    (s) => (counts[s.key as keyof RecordCounts] ?? 0) > 0,
  );

  if (visibleSections.length === 0) {
    return (
      <div className="flex flex-col flex-1 overflow-auto bg-white border-t border-gray-200 rounded-lg">
        <div className="p-4">
          <NoDataError message="No related records found." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-auto">
      <div className="bg-white border border-gray-200 rounded-lg">
        <AccordionPrimitive.Root
          type="multiple"
          value={expandedSections}
          onValueChange={setExpandedSections}
          className="w-full"
        >
          {visibleSections.map((section, index) => {
            const count =
              counts?.[section.key as keyof RecordCounts] ?? undefined;
            const isLast = index === visibleSections.length - 1;

            return (
              <AccordionPrimitive.Item
                key={section.key}
                value={section.key}
                className={cn(!isLast && 'border-b border-gray-200')}
              >
                <AccordionPrimitive.Header className="flex">
                  <AccordionPrimitive.Trigger
                    className={cn(
                      'flex w-full items-center gap-2 px-4 py-3',
                      'text-left text-sm font-semibold text-gray-800',
                      'transition-all outline-none',
                      'hover:bg-gray-50',
                      '[&[data-state=open]>svg]:rotate-0',
                      '[&[data-state=closed]>svg]:-rotate-90',
                    )}
                  >
                    <ChevronDownIcon className="text-red-500 transition-transform duration-200 size-4 shrink-0" />
                    <span>{section.label}</span>
                    {count !== undefined && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'h-5 min-w-5 justify-center rounded-full p-0 px-1.5 text-xs',
                          'bg-red-500 text-white hover:bg-red-500',
                        )}
                      >
                        {count}
                      </Badge>
                    )}
                  </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionPrimitive.Content>
                  <div className="pb-0">
                    <RelatedRecordSection
                      recordId={recordId}
                      gridType={section.gridType}
                      defaultColSettings={[...section.colSettings]}
                      fetchFn={section.fetchFn}
                      storageKey={section.storageKey}
                      cache={sectionCaches.current}
                    />
                  </div>
                </AccordionPrimitive.Content>
              </AccordionPrimitive.Item>
            );
          })}
        </AccordionPrimitive.Root>
      </div>
    </div>
  );
};
